import { useTranslation } from 'react-i18next'
import {
    BenchmarkingDisplay,
    BenchmarkingReportSettings,
    BenchmarkingRequestParams,
} from '@/hooks'
import { Option } from '@/components/common/Form/types'
import { RadioOptionPicker } from '@/components/common/Form/RadioOptionPicker/RadioOptionPicker'
import useAnalyticsStore from '@/features/Analytics/stores/useAnalyticsStore/useAnalyticsStore'
import { Pill, Tooltip } from '@/components/atoms'
import { useCallback, useRef } from 'react'
import { useBenchmarkingDisplayManager } from '../BenchmarkingReport/useBenchmarkingDisplayManager'

type BenchmarkingDisplaySettingProps = {
    filters: BenchmarkingRequestParams
    reportSettings: BenchmarkingReportSettings
    value: BenchmarkingDisplay
    onChange: (display: BenchmarkingDisplay) => void
}

export const BenchmarkingDisplaySetting: React.FC<
    BenchmarkingDisplaySettingProps
> = (props) => {
    const { value, onChange, reportSettings, filters } = props
    const { t } = useTranslation('translations')
    const pillRef = useRef<HTMLDivElement>(null)

    const setHoveredDisplaySetting = useAnalyticsStore(
        (state) => state.benchmarking.setHoveredDisplaySetting
    )

    const onMouseEnter = useCallback(
        (display: BenchmarkingDisplay) => {
            setHoveredDisplaySetting(display)
        },
        [setHoveredDisplaySetting]
    )

    const onMouseLeave = useCallback(() => {
        setHoveredDisplaySetting(null)
    }, [setHoveredDisplaySetting])

    const { showAlert, options } = useBenchmarkingDisplayManager(
        reportSettings,
        filters
    )

    const renderLabel = useCallback(
        (option: Option) => {
            const pillContent =
                'count' in option && typeof option.count === 'number'
                    ? option.count
                    : undefined

            return (
                <div
                    className="flex items-center gap-2"
                    onMouseEnter={() => {
                        onMouseEnter(option.value as BenchmarkingDisplay)
                    }}
                    onMouseLeave={onMouseLeave}
                >
                    <div className="text-label-small text-regular">
                        {option.label}
                    </div>
                    <Pill value={pillContent} />
                </div>
            )
        },
        [onMouseEnter, onMouseLeave]
    )

    const renderValueLabel = useCallback(
        (option: Option) => {
            const pillContent =
                'count' in option && typeof option.count === 'number'
                    ? option.count
                    : undefined

            return (
                <div className="flex items-center gap-2">
                    <div className="text-azure group-hover:text-blue-600 group-active:text-blue-700 group-disabled:text-disabled">
                        {option.label}
                    </div>
                    <div ref={pillRef}>
                        <Tooltip
                            content={t('analytics.benchmarking.alert')}
                            name="benchmarking-alert-tooltip"
                            show={showAlert}
                        >
                            <Pill
                                value={pillContent}
                                icon={showAlert ? 'info' : undefined}
                            />
                        </Tooltip>
                    </div>
                </div>
            )
        },
        [showAlert, t]
    )

    return (
        <RadioOptionPicker
            name="benchmarking-display-report-setting"
            label={t('analytics.benchmarking.display.label')}
            value={value}
            options={options}
            onChange={(display: string) => {
                onChange(display as BenchmarkingDisplay)
            }}
            renderLabel={renderLabel}
            renderValueLabel={renderValueLabel}
            className="!border-azure !shadow-inset-azure"
        />
    )
}
